import { z } from "zod";
/**
 * Book variants: My Story, Our Story, the Family Cookbook and the Comic, made on the Bedtime
 * engine (owner direction, 2026-10-15). Each variant keeps its own words pipeline (the guided
 * Studio stages) and shares one publication engine: a layout plan the server compiles with exact
 * geometry, pictures painted or placed into the plan's slots, a proof rendered from the plan, and
 * the buyer's approval of that exact proof.
 */
export const bookVariantIdSchema = z.enum(["MY_STORY", "OUR_STORY", "COOKBOOK", "COMIC"]);
export const BOOK_VARIANT_TEMPLATE_IDS = {
    story: "MY_STORY",
    love: "OUR_STORY",
    recipes: "COOKBOOK",
    comic: "COMIC",
};
export function bookVariantForTemplate(templateId) {
    return Object.hasOwn(BOOK_VARIANT_TEMPLATE_IDS, templateId)
        ? BOOK_VARIANT_TEMPLATE_IDS[templateId]
        : null;
}
/** Stored template identity chooses production. A native proof is never a fabricated canonical
 * fixed-profile package: each authority retains its own immutable source/file approval evidence.
 */
export function bookPublicationAuthorityForTemplate(templateId) {
    const variant = bookVariantForTemplate(templateId);
    if (variant)
        return { kind: "NATIVE_VARIANT", variant };
    if (templateId === "children")
        return { kind: "BEDTIME_PUBLICATION" };
    return { kind: "LEGACY" };
}
export const BOOK_VARIANT_PLAN_CONTRACT = "BOOK_VARIANT_PLAN_V1";
const idSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Za-z0-9:_.-]+$/u);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const timestampSchema = z.string().datetime({ offset: true });
/** Points from the trim box's top-left corner, y downward; bleed lies outside 0..trim. */
const pointSchema = z.number().finite().min(-200).max(2_000);
const sizeSchema = z.number().finite().positive().max(2_000);
const colorSchema = z.string().regex(/^#[0-9a-f]{6}$/u);
const opacitySchema = z.number().min(0).max(1);
export const variantTextRoleSchema = z.enum([
    "BODY",
    "HEADING",
    "DISPLAY",
    "KICKER",
    "CAPTION",
    "QUOTE",
    "ATTRIBUTION",
    "FOLIO",
    "RUNNING_HEAD",
    "LABEL",
    "INGREDIENT",
    "STEP_NUMBER",
    "STEP",
    "NOTE",
    "BALLOON",
    "CAPTION_BOX",
    "SFX",
    "DROP_CAP",
    "META",
]);
export const variantTextLineSchema = z
    .object({
    text: z.string().max(600),
    /** Left edge of the line's first glyph. */
    x: pointSchema,
    /** Baseline. */
    y: pointSchema,
    /** Extra space added at each word gap to justify the line, in points. */
    wordSpacing: z.number().min(0).max(60).optional(),
})
    .strict();
export const variantTextElementSchema = z
    .object({
    kind: z.literal("TEXT"),
    id: idSchema,
    role: variantTextRoleSchema,
    fontId: idSchema,
    size: sizeSchema,
    color: colorSchema,
    opacity: opacitySchema.optional(),
    letterSpacing: z.number().min(-5).max(40).optional(),
    /** 90: the text runs top to bottom (a spine), turned clockwise about each line's start. */
    rotation: z.literal(90).optional(),
    lines: z.array(variantTextLineSchema).max(160),
})
    .strict();
export const variantImageFrameSchema = z
    .object({
    /** A paper mount around the picture, as in a photo album or a plate page. */
    padding: z.number().min(0).max(60),
    color: colorSchema,
    border: colorSchema.optional(),
    borderWidth: z.number().min(0).max(8).optional(),
})
    .strict();
export const variantImageElementSchema = z
    .object({
    kind: z.literal("IMAGE"),
    id: idSchema,
    slotId: idSchema,
    x: pointSchema,
    y: pointSchema,
    width: sizeSchema,
    height: sizeSchema,
    /** COVER fills the box and crops; CONTAIN shows the whole picture inside it. */
    fit: z.enum(["COVER", "CONTAIN"]),
    focus: z
        .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
        .strict()
        .optional(),
    frame: variantImageFrameSchema.optional(),
    /** Comic panels and rounded plates clip to their own outline. */
    clipPath: z.string().max(8_000).optional(),
    /**
     * A painted edge that fades into the page, as Bedtime's pictures do (2026-10-24): the share of
     * the picture's height or width each edge fades over, into the page's colour.
     */
    soften: z
        .object({
        top: z.number().min(0).max(0.6).optional(),
        right: z.number().min(0).max(0.6).optional(),
        bottom: z.number().min(0).max(0.6).optional(),
        left: z.number().min(0).max(0.6).optional(),
        into: colorSchema,
    })
        .strict()
        .optional(),
})
    .strict();
export const variantShapeElementSchema = z
    .object({
    kind: z.literal("SHAPE"),
    id: idSchema,
    /** SVG path data in page points (M, L, C, Q, Z only). */
    path: z.string().min(1).max(40_000),
    fill: colorSchema.optional(),
    fillOpacity: opacitySchema.optional(),
    stroke: colorSchema.optional(),
    strokeWidth: z.number().min(0).max(40).optional(),
})
    .strict();
export const variantQrElementSchema = z
    .object({
    kind: z.literal("QR"),
    id: idSchema,
    x: pointSchema,
    y: pointSchema,
    size: sizeSchema,
    url: z.string().url().max(500),
    color: colorSchema,
})
    .strict();
export const variantElementSchema = z.discriminatedUnion("kind", [
    variantTextElementSchema,
    variantImageElementSchema,
    variantShapeElementSchema,
    variantQrElementSchema,
]);
export const variantPageSchema = z
    .object({
    pageNumber: z.number().int().positive().max(400),
    side: z.enum(["RECTO", "VERSO"]),
    /** What the page is for: HALF_TITLE, CHAPTER_OPENER, BODY, RECIPE, STORY_PAGE, … */
    role: z
        .string()
        .min(1)
        .max(60)
        .regex(/^[A-Z_]+$/u),
    /** The section a reader would say the page belongs to (a chapter or recipe id). */
    sectionId: idSchema.optional(),
    background: colorSchema,
    elements: z.array(variantElementSchema).max(500),
})
    .strict();
export const variantSlotPurposeSchema = z.enum([
    "CHAPTER_ART",
    "PHOTO",
    "FOOD",
    "PANEL",
    "COVER_ART",
    "CHARACTER_SHEET",
]);
export const variantMediaSlotSchema = z
    .object({
    slotId: idSchema,
    purpose: variantSlotPurposeSchema,
    /** The buyer's name for it: "Chapter 3 · The house on Hollis Street". */
    label: z.string().trim().min(1).max(160),
    /** The largest placed size on any page, in points. */
    placedWidth: sizeSchema,
    placedHeight: sizeSchema,
    /** What a painter is asked to paint, from the approved words; never shown as cost or jargon. */
    brief: z.string().trim().max(3_000),
    /**
     * AI paints it; PHOTO is the buyer's own picture; either lets the buyer choose. A slot's
     * current choice is in its media record, not here.
     */
    sources: z
        .array(z.enum(["AI", "PHOTO"]))
        .min(1)
        .max(2),
    defaultSource: z.enum(["AI", "PHOTO"]),
    /** Photos the buyer attached to the memory or recipe, offered first. */
    suggestedPhotoIds: z.array(idSchema).max(12),
    /** Painted before the rest, so the buyer approves the look on real pages first. */
    sample: z.boolean(),
    /** Painting order. */
    sequence: z.number().int().nonnegative().max(1_000),
    pageNumbers: z.array(z.number().int().positive().max(400)).min(1).max(8),
    /** Characters the painter must keep consistent (comic cast, by bible slot). */
    referenceSlotIds: z.array(idSchema).max(4),
    /**
     * Not needed for the first look. Every slot still needs its picture before the proof; a photo
     * plate whose photo is gone leaves the book when it is laid out again.
     */
    optional: z.boolean(),
    /**
     * What a painting here must show, set by the compiler: a chapter's title and era, a dish, a
     * panel's script, and the book's art style. A painting made for another identity is stale; a
     * small change to the words around it (a chapter's text, a headnote) is not.
     */
    identity: sha256Schema,
})
    .strict();
export const variantCoverSchema = z
    .object({
    design: z.enum(["MEMOIR", "COUPLE", "COOKBOOK", "COMIC"]),
    title: z.string().trim().min(1).max(160),
    subtitle: z.string().trim().max(200),
    byline: z.string().trim().max(200),
    spineText: z.string().trim().max(120),
    backCopy: z.string().trim().max(1_200),
    /** What the book is, for the back cover: a memoir's approved plan promise. */
    blurb: z.string().trim().min(1).max(1_200).optional(),
    /** Our Story's couple, by first name ("Walt & June"), on the front and the spine. */
    names: z.string().trim().min(1).max(160).optional(),
    /** Our Story's monogram, the couple's initials ("W & J"), whatever the title says. */
    monogram: z.string().trim().min(1).max(16).optional(),
    /** The comic's issue badge, the cookbook's family name, Our Story's "Since 1971"… */
    badge: z.string().trim().max(40),
    slotId: idSchema.optional(),
    paper: colorSchema,
    ink: colorSchema,
    accent: colorSchema,
    titleFontId: idSchema,
    textFontId: idSchema,
})
    .strict();
export const variantFormatSchema = z
    .object({
    trimWidth: sizeSchema,
    trimHeight: sizeSchema,
    bleed: z.number().min(0).max(36),
    /** Pages are added in these steps (the binder's signature). */
    pageMultiple: z.number().int().positive().max(16),
    minimumPages: z.number().int().positive().max(400),
    maximumPages: z.number().int().positive().max(400),
})
    .strict();
export const variantDiagnosticSchema = z
    .object({
    code: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[A-Z0-9_]+$/u),
    severity: z.enum(["INFO", "WARNING", "BLOCKING"]),
    message: z.string().min(1).max(500),
    pageNumber: z.number().int().positive().max(400).optional(),
    sectionId: idSchema.optional(),
    /**
     * Who the finding is about, as the family named them, so the page can act on it: a person
     * the family tree couldn't place (book quality batch M2). Absent from older plans.
     */
    subject: z.string().trim().min(1).max(120).optional(),
})
    .strict();
const letteringRectSchema = z
    .object({
    x: z.number().min(-200).max(2_000),
    y: z.number().min(-200).max(2_000),
    width: z.number().nonnegative().max(2_400),
    height: z.number().nonnegative().max(2_400),
})
    .strict();
/**
 * One comic panel's lettering as the compiler set it, so the proof can set it again around the faces
 * painted in its picture (2026-10-24). Words are the script's, never changed; only where they go.
 */
export const comicPanelLetteringSchema = z
    .object({
    slotId: idSchema,
    pageNumber: z.number().int().positive().max(400),
    rect: letteringRectSchema,
    /** What the words keep clear of: an inset over the panel's corner. */
    obstacles: z.array(letteringRectSchema).max(4),
    /** Room kept at the panel's top, for the issue's title on its first page. */
    reserveTop: z.number().nonnegative().max(1_000),
    characters: z.array(z.string().trim().min(1).max(40)).max(4),
    captions: z.array(z.string().trim().min(1).max(160)).max(2),
    // The comic balloon's shape (defined with the script, further down).
    balloons: z
        .array(z
        .object({
        speaker: z.string().trim().min(1).max(60),
        text: z.string().trim().min(1).max(160),
        kind: z.enum(["SPEECH", "THOUGHT", "SHOUT", "WHISPER"]),
    })
        .strict())
        .max(4),
    sfx: z.array(z.string().trim().min(1).max(24)).max(2),
    panel: z.number().int().positive().max(9),
    /** The page elements this lettering drew, in order: replaced when it is set again. */
    elementIds: z.array(z.string().min(1).max(60)).max(120),
})
    .strict();
export const bookVariantPlanV1Schema = z
    .object({
    contract: z.literal(BOOK_VARIANT_PLAN_CONTRACT),
    planId: idSchema,
    bookId: idSchema,
    variant: bookVariantIdSchema,
    /** The design the plan was laid out in (one look per type at launch). */
    lookId: idSchema,
    /** The book's own painting style, when the buyer chose one over the variant's default. */
    artStyle: z.string().trim().min(1).max(1_000).optional(),
    planRevision: z.number().int().positive(),
    /** The approved words, details and photos the plan was compiled from. */
    sourceDigest: sha256Schema,
    format: variantFormatSchema,
    fontIds: z.array(idSchema).max(40),
    pages: z.array(variantPageSchema).min(4).max(400),
    mediaSlots: z.array(variantMediaSlotSchema).max(240),
    cover: variantCoverSchema,
    diagnostics: z.array(variantDiagnosticSchema).max(300),
    /** The inked comic's lettering by panel, set again around the painted faces for the proof. */
    comicLettering: z.array(comicPanelLetteringSchema).max(200).optional(),
    createdAt: timestampSchema,
    planDigest: sha256Schema,
})
    .strict()
    .superRefine((plan, context) => {
    plan.pages.forEach((page, index) => {
        if (page.pageNumber !== index + 1)
            context.addIssue({
                code: "custom",
                path: ["pages", index, "pageNumber"],
                message: "Pages are numbered from 1 in order.",
            });
        if (page.side !== (page.pageNumber % 2 === 1 ? "RECTO" : "VERSO"))
            context.addIssue({
                code: "custom",
                path: ["pages", index, "side"],
                message: "Odd pages are rectos and even pages versos.",
            });
    });
    if (plan.pages.length % plan.format.pageMultiple !== 0)
        context.addIssue({
            code: "custom",
            path: ["pages"],
            message: "The page count must fill whole signatures.",
        });
    const slotIds = new Set(plan.mediaSlots.map(({ slotId }) => slotId));
    if (slotIds.size !== plan.mediaSlots.length)
        context.addIssue({ code: "custom", path: ["mediaSlots"], message: "Slot ids are unique." });
    for (const page of plan.pages)
        for (const element of page.elements)
            if (element.kind === "IMAGE" && !slotIds.has(element.slotId))
                context.addIssue({
                    code: "custom",
                    path: ["pages", page.pageNumber - 1],
                    message: `Page ${page.pageNumber} places an unknown picture slot.`,
                });
    if (plan.cover.slotId && !slotIds.has(plan.cover.slotId))
        context.addIssue({ code: "custom", path: ["cover"], message: "Unknown cover slot." });
});
const variantAssetSchema = z
    .object({
    objectKey: z.string().min(1).max(1_024),
    objectVersionId: z.string().min(1).max(1_024).optional(),
    checksumSha256: sha256Schema,
    contentType: z.enum(["image/png", "image/jpeg"]),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    /** Where the bytes live: painted pictures in the print bucket, photos in the media bucket. */
    bucket: z.enum(["PRINT", "MEDIA"]),
})
    .strict();
/** A picture placed in a slot: painted for this book, or the buyer's own photo. */
/**
 * A face the picture check saw in a painted comic panel: a box in fractions of the picture from its
 * top-left corner, and whose face it is when the cast's sheets tell (2026-10-24). The proof letters
 * the panel's balloons clear of these, with each tail toward its speaker's face.
 */
export const variantFaceSchema = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0).max(1),
    height: z.number().min(0).max(1),
    castId: z.string().trim().min(1).max(40).optional(),
})
    .strict();
const variantReviewSchema = z
    .object({
    verdict: z.enum(["PASS", "DOUBT", "FAIL"]),
    findings: z.array(z.string().max(300)).max(8),
    faces: z.array(variantFaceSchema).max(8).optional(),
    reviewedAt: timestampSchema,
})
    .strict();
export const variantMediaV1Schema = z
    .object({
    version: z.literal(1),
    bookId: idSchema,
    slotId: idSchema,
    source: z.enum(["AI", "PHOTO"]),
    state: z.enum(["READY", "FAILED"]),
    /** The slot this picture was made for; a picture keeps its slot across re-layouts. */
    slotDigest: sha256Schema,
    asset: variantAssetSchema.optional(),
    /** The last painting, kept while a photo is chosen, so changing back costs nothing. */
    paintedAsset: variantAssetSchema.optional(),
    /** The slot identity the kept painting was made for, so switching back to it stays honest. */
    paintedSlotDigest: sha256Schema.optional(),
    /** The kept painting's own check, restored with it, so switching back never skips a FAIL. */
    paintedReview: variantReviewSchema.optional(),
    photoId: idSchema.optional(),
    /** Effective resolution at the largest placed size. */
    effectiveDpi: z.number().nonnegative().max(5_000).optional(),
    review: variantReviewSchema.optional(),
    /** Paintings for this slot so far; at most two are automatic (decision 5). */
    paintCount: z.number().int().nonnegative().max(20),
    automaticRepaints: z.number().int().nonnegative().max(2),
    /** The buyer kept a doubted picture after its automatic repaints. */
    keptByBuyer: z.boolean(),
    buyerNote: z.string().trim().max(300).optional(),
    failureReason: z.string().max(300).optional(),
    updatedAt: timestampSchema,
})
    .strict();
export const variantJobActionSchema = z.enum([
    "PAINT",
    "RENDER_PROOF",
    "SCRIPT",
    "FOLLOW_UPS",
    /** A photographed recipe card written down for the family to check (the cookbook). */
    "CARD",
]);
/**
 * Questions that draw more out of one memory (owner decision D3): asked from the storyteller's own
 * words only, so a chapter can be fuller without anything being invented.
 */
export const variantFollowUpsV1Schema = z
    .object({
    version: z.literal(1),
    chapterId: idSchema,
    questions: z.array(z.string().trim().min(1).max(200)).min(1).max(2),
    /** How many words the memory had when these were asked. */
    askedAtWords: z.number().int().nonnegative(),
    createdAt: timestampSchema,
})
    .strict();
/**
 * What a comic tells (owner decision 3, 2026-11-02): one true moment, such as a night, a rescue or
 * a birth, or a whole life. A comic with none stored is a life story, as every comic was before.
 */
export const comicStorySchema = z.enum(["MOMENT", "LIFE"]);
/** The life story's core questions, by the comic template's positions: the arc it needs. */
export const COMIC_LIFE_CORE_POSITIONS = [0, 1, 2, 3, 5, 6, 7, 10];
/**
 * The one true moment's questions (owner decision 3). Each is answered in the chapter at its
 * position, as the life story's questions are, so a moment needs no records of its own; a comic's
 * story is chosen before its first answer and kept after it. `topic` heads the answer in Behind
 * the story.
 */
export const COMIC_MOMENT_QUESTIONS = [
    {
        position: 0,
        question: "Who was there? Name everyone, who they were to each other, and what each one did.",
        topic: "Who was there",
        core: true,
    },
    {
        position: 1,
        question: "Where and when did it happen? The place, the date or season, the time of day and the weather.",
        topic: "Where and when",
        core: true,
    },
    {
        position: 2,
        question: "What happened, from the very start? Tell it in order, one thing after another.",
        topic: "What happened",
        core: true,
    },
    {
        position: 3,
        question: "What was the hardest moment?",
        topic: "The hardest moment",
        core: true,
    },
    {
        position: 5,
        question: "What did people say? Any words you remember, even roughly, and who said them.",
        topic: "What was said",
        core: true,
    },
    { position: 6, question: "How did it end?", topic: "How it ended", core: true },
    {
        position: 7,
        question: "What came after, the next day or years later?",
        topic: "What came after",
        core: true,
    },
    {
        position: 4,
        question: "What small things do you remember? Sounds, smells, what people wore, a joke someone told.",
        topic: "The small things",
        core: false,
    },
    {
        position: 8,
        question: "Why does this moment matter to your family?",
        topic: "Why it matters",
        core: false,
    },
];
/** The positions a comic's story needs answered before its script is written. */
export function comicCorePositions(story) {
    return story === "MOMENT"
        ? COMIC_MOMENT_QUESTIONS.filter(({ core }) => core).map(({ position }) => position)
        : COMIC_LIFE_CORE_POSITIONS;
}
/** The moment's question at a template position, if the moment asks one there. */
export function comicMomentQuestion(position) {
    return COMIC_MOMENT_QUESTIONS.find((entry) => entry.position === position);
}
/**
 * The Comic's script: one issue of 24 story pages in three acts, from the family's answers to the
 * hero prompts. Words are live lettering, never painted into the art.
 */
export const COMIC_STORY_PAGES = 24;
/** The most lettering one comic page may carry, captions and balloons together. */
export const COMIC_PAGE_LETTERING_CHARACTERS = 1_400;
/** The most a whole comic script may weigh as JSON, art notes and all. */
export const COMIC_SCRIPT_MAXIMUM_CHARACTERS = 350_000;
/**
 * The lettering one panel can hold at the 8-point floor, by how many panels share its page: a
 * splash holds the most, a six-panel page's panels the least (measured on the page grid).
 */
export function comicPanelLetteringCharacters(panelsOnPage) {
    return panelsOnPage <= 1
        ? 900
        : panelsOnPage <= 2
            ? 600
            : panelsOnPage <= 3
                ? 480
                : panelsOnPage <= 4
                    ? 380
                    : 280;
}
export const comicShotSchema = z.enum(["ESTABLISHING", "WIDE", "MEDIUM", "CLOSE", "EXTREME_CLOSE"]);
export const comicBalloonSchema = z
    .object({
    speaker: z.string().trim().min(1).max(60),
    text: z.string().trim().min(1).max(160),
    kind: z.enum(["SPEECH", "THOUGHT", "SHOUT", "WHISPER"]),
})
    .strict();
/**
 * The art director's notes on one panel (2026-10-22): what the painter needs and the words leave
 * out. Never lettered and never shown as the story; a script written before them has none.
 */
export const comicPanelArtSchema = z
    .object({
    /** The angle and distance: a low angle, an over-the-shoulder shot, an extreme close-up. */
    camera: z.string().trim().max(300),
    /** The time of day, the light sources and the mood; a dark scene stays dark. */
    light: z.string().trim().max(300),
    /** The year or decade, and what it means for clothes, cars and buildings. */
    period: z.string().trim().max(300),
    /** Everyone in the panel who isn't cast, each unlike every cast member. */
    extras: z.string().trim().max(500),
    /** What the cast in the panel wear, the same through a scene. */
    wardrobe: z.string().trim().max(400),
})
    .strict();
export const comicPanelSchema = z
    .object({
    panel: z.number().int().positive().max(9),
    shot: comicShotSchema,
    /** Where and when, only as the answers tell it. */
    setting: z.string().trim().max(200),
    /** What the reader sees happen, for the painter. */
    action: z.string().trim().min(1).max(500),
    /** Cast present, by castId. */
    characters: z.array(z.string().trim().min(1).max(40)).max(4),
    captions: z.array(z.string().trim().min(1).max(160)).max(2),
    balloons: z.array(comicBalloonSchema).max(4),
    sfx: z.array(z.string().trim().min(1).max(24)).max(2),
    art: comicPanelArtSchema.optional(),
})
    .strict();
export const comicPageSchema = z
    .object({
    page: z.number().int().positive().max(COMIC_STORY_PAGES),
    act: z.union([z.literal(1), z.literal(2), z.literal(3)]),
    beat: z.string().trim().min(1).max(400),
    /** One picture across the whole page, for the big moments. */
    splash: z.boolean(),
    panels: z.array(comicPanelSchema).min(1).max(6),
})
    .strict()
    .superRefine((page, context) => {
    // Every page stays legible, and a whole script stays well inside one stored record.
    const lettering = page.panels.reduce((total, panel) => total +
        panel.captions.reduce((sum, caption) => sum + caption.length, 0) +
        panel.balloons.reduce((sum, balloon) => sum + balloon.text.length, 0), 0);
    if (lettering > COMIC_PAGE_LETTERING_CHARACTERS)
        context.addIssue({
            code: "custom",
            path: ["panels"],
            message: `Page ${page.page} has too many words; keep its captions and balloons under ${COMIC_PAGE_LETTERING_CHARACTERS} characters.`,
        });
});
/** The most a family's description of how someone looks may carry: a cast member's own limit. */
export const COMIC_HERO_LOOK_CHARACTERS = 500;
/**
 * What never changes about one person through the comic (2026-11-02, C2), as data, so every
 * painter's brief and picture check says the same thing: their skin, hair, eyes and build as the
 * family gave them (empty where the family said nothing: their character sheet then carries it),
 * what they do in the story (who drives), and what they wear, by the pages each outfit holds for.
 */
export const comicCastConstantsSchema = z
    .object({
    skin: z.string().trim().max(80),
    hair: z.string().trim().max(120),
    eyes: z.string().trim().max(80),
    build: z.string().trim().max(120),
    /** What they do in the story, the same on every page: "drives the ambulance". */
    duty: z.string().trim().max(160),
    outfits: z
        .array(z
        .object({
        fromPage: z.number().int().min(1).max(COMIC_STORY_PAGES),
        toPage: z.number().int().min(1).max(COMIC_STORY_PAGES),
        clothes: z.string().trim().min(1).max(200),
    })
        .strict())
        .max(4),
})
    .strict();
export const comicCastMemberSchema = z
    .object({
    castId: z
        .string()
        .trim()
        .min(1)
        .max(40)
        .regex(/^[a-z0-9-]+$/u),
    name: z.string().trim().min(1).max(60),
    role: z.enum(["HERO", "SIDEKICK", "NEMESIS", "FAMILY", "FRIEND", "MENTOR", "OTHER"]),
    /** How they look, for the character sheet: age, build, hair, clothes, one signature detail. */
    look: z.string().trim().min(1).max(COMIC_HERO_LOOK_CHARACTERS),
    /** When the same person is cast at more than one age, which this is: "age 8", "grown up". */
    stage: z.string().trim().min(1).max(40).optional(),
    /**
     * Who they are in the story, in a few plain words for the cast page ("Mom-to-be", "EMT
     * partner"), from a script written since 2026-11-02; never the role's code word.
     */
    part: z.string().trim().min(1).max(40).optional(),
    /** The story page where they first appear in person (a script written since 2026-11-02). */
    firstPage: z.number().int().min(1).max(COMIC_STORY_PAGES).optional(),
    constants: comicCastConstantsSchema.optional(),
})
    .strict();
/** A note the comic's editor left for the family: something it couldn't put right itself. */
export const comicEditorNoteSchema = z
    .object({
    page: z.number().int().min(1).max(COMIC_STORY_PAGES),
    note: z.string().trim().min(1).max(240),
})
    .strict();
export const comicScriptV1Schema = z
    .object({
    version: z.literal(1),
    title: z.string().trim().min(1).max(120),
    tagline: z.string().trim().max(160),
    cast: z.array(comicCastMemberSchema).min(1).max(6),
    pages: z.array(comicPageSchema).length(COMIC_STORY_PAGES),
    /** What came after the story, from the last answer (printed as the epilogue). */
    epilogue: z.string().trim().max(800),
    /** What the comic tells; a script without it was written as a life story. */
    story: comicStorySchema.optional(),
    /**
     * The writer's prompt version the script was written with (since 2026-11-02): a comic first laid
     * out from such a script takes the look made for it; one without keeps today's.
     */
    promptVersion: z.string().trim().min(1).max(40).optional(),
    /** What the editor couldn't put right itself, for the family to look at (C3). */
    editorNotes: z.array(comicEditorNoteSchema).max(12).optional(),
    /** The art director's cover: who it shows, at the story's peak, in its own place and time. */
    coverArt: z
        .object({
        castId: z.string().trim().min(1).max(40),
        scene: z.string().trim().min(1).max(600),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((script, context) => {
    const cast = new Set(script.cast.map(({ castId }) => castId));
    if (cast.size !== script.cast.length)
        context.addIssue({ code: "custom", path: ["cast"], message: "Cast ids are unique." });
    if (script.coverArt && !cast.has(script.coverArt.castId))
        context.addIssue({
            code: "custom",
            path: ["coverArt"],
            message: `Unknown cast member ${script.coverArt.castId}.`,
        });
    // A script with the art director's notes stays well inside one stored record (400 KB).
    if (JSON.stringify(script).length > COMIC_SCRIPT_MAXIMUM_CHARACTERS)
        context.addIssue({ code: "custom", path: [], message: "The script is too long to keep." });
    script.pages.forEach((page, index) => {
        if (page.page !== index + 1)
            context.addIssue({
                code: "custom",
                path: ["pages", index],
                message: "Pages run 1 to 24 in order.",
            });
        if (page.splash && page.panels.length !== 1)
            context.addIssue({
                code: "custom",
                path: ["pages", index],
                message: "A splash page has one panel.",
            });
        for (const panel of page.panels)
            for (const id of panel.characters)
                if (!cast.has(id))
                    context.addIssue({
                        code: "custom",
                        path: ["pages", index],
                        message: `Unknown cast member ${id}.`,
                    });
    });
    if (script.pages.filter(({ splash }) => splash).length > 3)
        context.addIssue({ code: "custom", path: ["pages"], message: "At most three splash pages." });
});
/** A stored script: what it was written from, and the buyer's approval of these exact words. */
export const variantScriptV1Schema = z
    .object({
    version: z.literal(1),
    scriptId: idSchema,
    script: comicScriptV1Schema,
    sourceDigest: sha256Schema,
    createdAt: timestampSchema,
    approvedAt: timestampSchema.optional(),
    approvedBy: idSchema.optional(),
})
    .strict();
/**
 * A recipe card the family photographed, written down word for word: evidence for them to check
 * and keep, never an authority. What couldn't be read is listed, never guessed.
 */
export const variantRecipeCardV1Schema = z
    .object({
    version: z.literal(1),
    recipeId: idSchema,
    photoId: idSchema,
    title: z.string().trim().max(200),
    text: z.string().max(20_000),
    unclear: z.array(z.string().trim().min(1).max(200)).max(20),
    createdAt: timestampSchema,
})
    .strict();
const variantJobFields = {
    version: z.literal(1),
    id: idSchema,
    bookId: idSchema,
    action: variantJobActionSchema,
    slotIds: z.array(idSchema).max(8),
    planDigest: sha256Schema,
    /** The exact retained output; a proof job ID is not its final proof ID. Opt-in public field. */
    proofId: idSchema.optional(),
    state: z.enum(["QUEUED", "RUNNING", "SUCCEEDED", "NEEDS_ATTENTION"]),
    failureReason: z.string().max(300).optional(),
    /** Pages showing this job's pictures, drawn for the buyer to see them in place. */
    pagePreviews: z
        .array(z
        .object({
        pageNumber: z.number().int().positive().max(400),
        objectKey: z.string().max(1_024),
    })
        .strict())
        .max(16)
        .optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
};
/** The server's job record. */
export const variantJobV1Schema = z
    .object({
    ...variantJobFields,
    requestedBy: idSchema,
    /** Paint the next unpainted pictures when this batch is done, with the page closed. */
    continueAutomatically: z.boolean().optional(),
    buyerNote: z.string().trim().max(300).optional(),
    reservedCostMicros: z.number().int().nonnegative().optional(),
    actualCostMicros: z.number().int().nonnegative().optional(),
    /** A long job (the comic's script) runs in parts, each in its own workflow run. */
    part: z.number().int().min(1).max(20).optional(),
    /** What the job's earlier parts spent, settled with the last part. */
    spentBeforeMicros: z.number().int().nonnegative().optional(),
    /** Parts that stopped on a provider error rather than their time limit. */
    providerStops: z.number().int().nonnegative().max(20).optional(),
})
    .strict();
/** A job as the buyer's page sees it: no price, no internal detail. */
export const variantJobViewV1Schema = z.object(variantJobFields).strict();
/** One of a proof's two files. */
const variantProofFileSchema = z
    .object({
    objectKey: z.string().min(1).max(1_024),
    checksumSha256: sha256Schema,
    byteSize: z.number().int().positive(),
    /** The exact stored version a print order binds (proofs from 2026-10-16 on). */
    objectVersionId: z.string().min(1).max(1_024).optional(),
    /** Every page's size, in points, as drawn. */
    widthPoints: z.number().positive().max(5_000).optional(),
    heightPoints: z.number().positive().max(5_000).optional(),
})
    .strict();
export const variantProofV1Schema = z
    .object({
    version: z.literal(1),
    proofId: idSchema,
    bookId: idSchema,
    planDigest: sha256Schema,
    /** Every picture the proof printed, by slot. */
    mediaDigest: sha256Schema,
    pageCount: z.number().int().positive().max(400),
    interior: variantProofFileSchema,
    cover: variantProofFileSchema,
    /**
     * The printed book this proof is the exact file for: Gelato's photobook profile for its size
     * and length. Absent when the book can't be printed (or the proof predates printing).
     */
    print: z
        .object({
        profileId: z.string().min(1).max(200),
        profileVersion: z.string().min(1).max(200),
        pageCount: z.number().int().positive().max(400),
    })
        .strict()
        .optional(),
    /** One small picture of each page for reading on screen, by page number. */
    pagePreviewKeys: z.array(z.string().min(1).max(1_024)).max(400),
    coverPreviewKey: z.string().min(1).max(1_024),
    createdAt: timestampSchema,
})
    .strict();
export const variantEditionV1Schema = z
    .object({
    version: z.literal(1),
    bookId: idSchema,
    proofId: idSchema,
    planDigest: sha256Schema,
    mediaDigest: sha256Schema,
    approvedBy: idSchema,
    approvedAt: timestampSchema,
    /** The book's revision the buyer approved; its print order is for that revision. */
    bookRevision: z.number().int().nonnegative().optional(),
    /**
     * This approval's own identity (from 2026-10-16): each approval is its own print source,
     * `variant-edition:<editionId>`, so approving again never disturbs a package or an order.
     */
    editionId: idSchema.optional(),
})
    .strict();
/** A variant edition's identity as a print source: the approval itself. */
export const variantEditionPrintSourceId = (editionId) => `variant-edition:${editionId}`;
/**
 * Where the buyer's included printed copy stands, for the Book step:
 * - NOT_PRINTABLE: this book's size or length has no printed edition;
 * - NO_PRINTED_COPY: the book has no included printed copy to order;
 * - AWAITING_PROVIDER_ACTIVATION: ordering printed copies isn't open yet;
 * - PREPARING: the print file is being checked and packaged;
 * - READY_FOR_DELIVERY: the buyer can give an address and send it to print;
 * - ORDERED: the included copy is ordered (`physicalOrderId`);
 * - NEEDS_ATTENTION: the package couldn't be prepared; preparing it again may help.
 */
export const variantDeliveryV1Schema = z
    .object({
    state: z.enum([
        "NOT_PRINTABLE",
        "NO_PRINTED_COPY",
        "AWAITING_PROVIDER_ACTIVATION",
        "PREPARING",
        "READY_FOR_DELIVERY",
        "ORDERED",
        "NEEDS_ATTENTION",
    ]),
    previewId: z.string().min(1).max(80),
    bookRevision: z.number().int().nonnegative(),
    entitlementId: z.string().min(1).max(80).nullable(),
    physicalOrderId: z.string().min(1).max(80).nullable(),
})
    .strict();
/**
 * The physical order the buyer started for an approved edition, so the Book step can link to it.
 * Only a pointer: the order itself (and its page) is the authority on where it stands.
 */
export const variantPrintOrderV1Schema = z
    .object({
    version: z.literal(1),
    previewId: z.string().min(1).max(80),
    physicalOrderId: idSchema,
    recordedAt: timestampSchema,
})
    .strict();
/**
 * Pictures in one painting job, painted at once (three at once painted a live comic's 88 pictures
 * with no rate limit or provider error, about 28 s a batch, 2026-10-16). A book's own budget
 * policy prices its batch, so the state names the limit for each book (`paintBatchLimit`): three,
 * or six for a comic started from 2026-10-16. A larger batch than its policy prices would split
 * the same money across more pictures and leave none for their automatic repaints.
 */
export const VARIANT_PAINT_BATCH_LIMIT = 3;
/** The most pictures any policy prices into one painting job. */
export const VARIANT_PAINT_BATCH_LIMIT_MAXIMUM = 6;
/**
 * The note a memoir's giver writes at its front (2026-10-18 audit, D4): who it is for, who it is
 * from, and a few words, set as an inscription on the first page.
 */
export const variantInscriptionV1Schema = z
    .object({
    version: z.literal(1),
    to: z.string().trim().max(80),
    from: z.string().trim().max(80),
    note: z.string().trim().max(600),
    updatedAt: timestampSchema,
})
    .strict();
/** Where someone goes on a My Story family tree, or NONE to keep them off it. */
export const variantTreePlaceSchema = z.enum(["PARENT", "SPOUSE", "CHILD", "GRANDCHILD", "NONE"]);
/**
 * Where the family put people on a My Story family tree themselves (book quality batch M2): the
 * tree couldn't place them from the relationship typed, or they are named only at the book's
 * front. Layout only: it never changes the storyteller details the chapters were written from.
 */
export const variantTreePlacesV1Schema = z
    .object({
    version: z.literal(1),
    places: z
        .array(z
        .object({ name: z.string().trim().min(1).max(80), place: variantTreePlaceSchema })
        .strict())
        .max(24),
    updatedAt: timestampSchema,
})
    .strict();
/**
 * The picture a memoir's cover carries (2026-10-18 audit, E3): a chapter's painting or photo, or
 * the portrait facing the title page. With none chosen, the first chapter's. A cookbook made in
 * its second look chooses its cover dish the same way (K6): `chapterId` names the recipe, and with
 * none chosen the cover carries the first dish.
 */
export const variantCoverChoiceV1Schema = z
    .object({
    version: z.literal(1),
    chapterId: z.string().min(1).max(160).nullable(),
    portrait: z.boolean(),
    updatedAt: timestampSchema,
})
    .strict();
/** One chapter's printed code: the family's private listening link to its recording. */
const variantVoiceCodeSchema = z
    .object({
    chapterId: z.string().min(1).max(160),
    shareId: idSchema,
    url: z.string().url().max(2_048),
})
    .strict();
/**
 * A memoir's voice codes (2026-10-18 audit, B3): each spoken chapter prints a code that plays the
 * storyteller telling it, through a private listening link the owner can turn off.
 */
export const variantVoiceCodesV1Schema = z
    .object({
    version: z.literal(1),
    codes: z.array(variantVoiceCodeSchema).max(60),
    /** Who agreed, for the storyteller, that anyone holding the book may listen. */
    consentedBy: idSchema.optional(),
    updatedAt: timestampSchema,
})
    .strict();
/** What the guided Pictures and Book steps read. Never carries a price. */
export const bookVariantStateV1Schema = z
    .object({
    variant: bookVariantIdSchema,
    bookRevision: z.number().int().nonnegative(),
    /** Why the book cannot be laid out yet, in the buyer's words; empty when it can. */
    blockers: z.array(z.string().max(300)).max(20),
    plan: bookVariantPlanV1Schema.nullable(),
    /** The plan was made from the words and photos as they stand now. */
    planCurrent: z.boolean(),
    lookApproved: z.boolean(),
    slots: z.array(z
        .object({
        slotId: idSchema,
        media: variantMediaV1Schema.nullable(),
        /** A short-lived link to the picture, for showing it. */
        imageUrl: z.string().url().max(4_000).nullable(),
        /** The picture belongs to the plan as it stands. */
        current: z.boolean(),
    })
        .strict()),
    jobs: z.array(variantJobViewV1Schema).max(50),
    /** Short-lived links to the pages drawn for the newest painting jobs, by page number. */
    pagePreviewUrls: z
        .array(z
        .object({ pageNumber: z.number().int().positive(), url: z.string().url().max(4_000) })
        .strict())
        .max(32),
    proof: variantProofV1Schema.nullable(),
    proofCurrent: z.boolean(),
    proofUrls: z
        .object({
        interior: z.string().url().max(4_000),
        cover: z.string().url().max(4_000),
        coverPreview: z.string().url().max(4_000),
        pages: z.array(z.string().url().max(4_000)).max(400),
    })
        .strict()
        .nullable(),
    edition: variantEditionV1Schema.nullable(),
    /** The last accepted edition remains immutable and visible even after newer words make it stale. */
    acceptedEdition: variantEditionV1Schema.nullable().optional(),
    /** Opt-in native read: this reader owns the book and the accepted edition was owner-approved. */
    printApprovalByOwner: z.boolean().optional(),
    /** The latest retained proof identity, even when it no longer matches the current source/plan. */
    latestProof: variantProofV1Schema.nullable().optional(),
    /** The approved edition's printed copy, once the edition is approved. */
    delivery: variantDeliveryV1Schema.nullable().optional(),
    /** The book can still pay for more pictures. */
    canPaint: z.boolean(),
    /** How many pictures one painting job may carry for this book. */
    paintBatchLimit: z.number().int().min(1).max(VARIANT_PAINT_BATCH_LIMIT_MAXIMUM).optional(),
    settings: z
        .object({
        pictureStyle: z.enum(["ILLUSTRATED", "PHOTOGRAPHIC"]),
        heroLook: z.string().trim().min(1).max(COMIC_HERO_LOOK_CHARACTERS).optional(),
        /** A comic's story, once chosen (state version 3). */
        comicStory: comicStorySchema.optional(),
    })
        .strict()
        .optional(),
    /** Follow-up questions asked for each memory, newest per memory. */
    followUps: z.array(variantFollowUpsV1Schema).max(60).optional(),
    /** The giver's note at the front of a memoir, once written. */
    inscription: variantInscriptionV1Schema.optional(),
    /** Where the family put people on a My Story family tree themselves, once they did (M2). */
    treePlaces: variantTreePlacesV1Schema.optional(),
    /** A memoir's chosen cover picture, once chosen. */
    coverChoice: variantCoverChoiceV1Schema.optional(),
    /** A memoir's printed voice codes, once added. */
    voiceCodes: variantVoiceCodesV1Schema.optional(),
    /** A comic's script, while it is written, read and approved. */
    script: variantScriptV1Schema.nullable().optional(),
    /** The script was written from the answers as they stand now. */
    scriptCurrent: z.boolean().optional(),
    /** A guided cookbook's recipe approvals, by recipe. */
    recipeApprovals: z
        .array(z
        .object({ recipeId: z.string().min(1).max(160), revision: z.number().int().positive() })
        .strict())
        .max(200)
        .optional(),
    /** A guided cookbook's photographed recipe cards, written down, by recipe. */
    recipeCards: z.array(variantRecipeCardV1Schema).max(200).optional(),
    /** POST-only receipt for the exact paint request, including an affordability-trimmed batch. */
    paintReceipt: z
        .object({
        jobId: idSchema,
        planDigest: sha256Schema,
        slotIds: z.array(idSchema).min(1).max(VARIANT_PAINT_BATCH_LIMIT_MAXIMUM),
    })
        .strict()
        .optional(),
    proofReceipt: z
        .object({ jobId: idSchema, planDigest: sha256Schema, proofId: idSchema.nullable() })
        .strict()
        .optional(),
    designReceipt: z
        .object({
        action: z.enum(["SET_SETTINGS", "SET_COVER", "SET_INSCRIPTION", "SET_TREE_PLACES"]),
        sourceBookRevision: z.number().int().nonnegative(),
        committedBookRevision: z.number().int().nonnegative(),
        commandDigest: sha256Schema,
    })
        .strict()
        .optional(),
})
    .strict();
/** The most times one picture is painted (the automatic repaints included). */
export const VARIANT_MAXIMUM_PAINTS_PER_SLOT = 6;
/** Findings that never print: words painted in, or a memoir portrait of a real person's face. */
const LETTERING_FINDING = /lettering or signs/iu;
const FACE_FINDING = /close-up face|face is clearly shown/iu;
/**
 * Whether the family may keep a picture as it is. A memoir face never prints (a photo always
 * can stand in). Painted-in words print only once the picture can't be painted again, so a
 * comic panel, which takes no photo, never leaves the family with no way forward.
 */
export function variantPictureKeepable(input) {
    if (input.verdict !== "FAIL")
        return true;
    if (input.findings.some((finding) => FACE_FINDING.test(finding)))
        return false;
    if (input.findings.some((finding) => LETTERING_FINDING.test(finding)))
        return input.paintCount >= VARIANT_MAXIMUM_PAINTS_PER_SLOT || !input.canPaintMore;
    return true;
}
/** Shared admission for a buyer's reviewed choice; assets/source identities are checked separately. */
export function variantPictureReviewResolved(input) {
    if (input.source === "PHOTO" || !input.review || input.review.verdict === "PASS")
        return true;
    return (input.keptByBuyer &&
        variantPictureKeepable({
            verdict: input.review.verdict,
            findings: input.review.findings,
            paintCount: input.paintCount,
            canPaintMore: false,
        }));
}
export const bookVariantCommandV1Schema = z.discriminatedUnion("action", [
    z
        .object({
        action: z.literal("COMPILE"),
        expectedBookRevision: z.number().int().nonnegative(),
    })
        .strict(),
    z
        .object({
        action: z.literal("PAINT"),
        planDigest: sha256Schema,
        slotIds: z
            .array(idSchema)
            .min(1)
            .max(VARIANT_PAINT_BATCH_LIMIT_MAXIMUM)
            .refine((ids) => new Set(ids).size === ids.length, "Each picture place may appear only once in a batch."),
    })
        .strict(),
    z
        .object({
        action: z.literal("APPROVE_LOOK"),
        planDigest: sha256Schema,
    })
        .strict(),
    z
        .object({
        action: z.literal("USE_PHOTO"),
        slotId: idSchema,
        photoId: idSchema,
        planDigest: sha256Schema.optional(),
        expectedUpdatedAt: timestampSchema.nullable().optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal("USE_PAINTING"),
        slotId: idSchema,
        planDigest: sha256Schema.optional(),
        expectedUpdatedAt: timestampSchema.nullable().optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal("KEEP"),
        slotId: idSchema,
        planDigest: sha256Schema.optional(),
        expectedUpdatedAt: timestampSchema.nullable().optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal("REPAINT"),
        slotId: idSchema,
        planDigest: sha256Schema,
        note: z.string().trim().max(300).optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal("RENDER_PROOF"),
        planDigest: sha256Schema,
    })
        .strict(),
    z
        .object({
        action: z.literal("APPROVE_EDITION"),
        proofId: idSchema,
    })
        .strict(),
    z
        .object({
        /** Package the approved edition's print file again, after printing opened or a failure. */
        action: z.literal("PREPARE_DELIVERY"),
        proofId: idSchema,
    })
        .strict(),
    z
        .object({
        /** The buyer started the included copy's print order for this approved edition. */
        action: z.literal("RECORD_PRINT_ORDER"),
        proofId: idSchema,
        physicalOrderId: idSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal("SET_SETTINGS"),
        expectedBookRevision: z.number().int().nonnegative().optional(),
        /** The cookbook's pictures: illustrations (the default) or, by choice, photographs. */
        pictureStyle: z.enum(["ILLUSTRATED", "PHOTOGRAPHIC"]),
        /**
         * A comic's hero as the family describes them, for the script's writer (E14). Empty takes it
         * away; left out, the description already saved is kept.
         */
        heroLook: z.string().trim().max(COMIC_HERO_LOOK_CHARACTERS).optional(),
        /**
         * What a comic tells, chosen before its first answer (owner decision 3). Left out, the
         * story already saved is kept.
         */
        comicStory: comicStorySchema.optional(),
    })
        .strict(),
    z
        .object({
        action: z.literal("ASK_FOLLOW_UPS"),
        chapterId: idSchema,
    })
        .strict(),
    z
        .object({
        /**
         * A memoir's voice codes: the chapters' listening links, printed as codes. Adding them needs
         * the owner's agreement, for the storyteller; none takes them out.
         */
        action: z.literal("SET_VOICE_CODES"),
        codes: z.array(variantVoiceCodeSchema).max(60),
        consent: z.literal(true).optional(),
    })
        .strict(),
    z
        .object({
        /**
         * A memoir's cover picture: a chapter's, the portrait's, or (neither) the first chapter's. A
         * second-look cookbook's: the recipe (`chapterId`) whose dish it carries, or the first.
         */
        action: z.literal("SET_COVER"),
        expectedBookRevision: z.number().int().nonnegative().optional(),
        chapterId: z.string().min(1).max(160).nullable(),
        portrait: z.boolean(),
    })
        .strict(),
    z
        .object({
        /** A memoir's (or a second-look cookbook's) inscription: all three empty takes it away. */
        action: z.literal("SET_INSCRIPTION"),
        expectedBookRevision: z.number().int().nonnegative().optional(),
        to: z.string().trim().max(80),
        from: z.string().trim().max(80),
        note: z.string().trim().max(600),
    })
        .strict(),
    z
        .object({
        /**
         * Where people go on a My Story family tree, by name (M2): replaces every earlier choice, so
         * an empty list takes them all back.
         */
        action: z.literal("SET_TREE_PLACES"),
        expectedBookRevision: z.number().int().nonnegative().optional(),
        places: variantTreePlacesV1Schema.shape.places,
    })
        .strict(),
    z
        .object({
        action: z.literal("WRITE_SCRIPT"),
        expectedBookRevision: z.number().int().nonnegative(),
        /**
         * Writing again over a script the family already has replaces its words, their changes
         * included, and is paid from the book's allowance: only ever on their explicit say-so (E12).
         */
        replaceScript: z.literal(true).optional(),
    })
        .strict(),
    z
        .object({
        /** The buyer's edits to the words: balloons, captions, the title. */
        action: z.literal("SAVE_SCRIPT"),
        scriptId: idSchema,
        script: comicScriptV1Schema,
    })
        .strict(),
    z
        .object({
        action: z.literal("APPROVE_SCRIPT"),
        scriptId: idSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal("RESTORE_SCRIPT"),
        historyId: idSchema,
        expectedScriptId: idSchema,
    })
        .strict(),
    z
        .object({
        action: z.literal("APPROVE_RECIPE"),
        recipeId: z.string().min(1).max(160),
        revision: z.number().int().positive(),
    })
        .strict(),
    z
        .object({
        action: z.literal("READ_RECIPE_CARD"),
        recipeId: idSchema,
        photoId: idSchema,
    })
        .strict(),
]);
export const variantSettingsV1Schema = z
    .object({
    version: z.literal(1),
    pictureStyle: z.enum(["ILLUSTRATED", "PHOTOGRAPHIC"]),
    /** A comic's hero, as the family describes them (E14). */
    heroLook: z.string().trim().min(1).max(COMIC_HERO_LOOK_CHARACTERS).optional(),
    /** What a comic tells (owner decision 3); none stored is a life story. */
    comicStory: comicStorySchema.optional(),
    updatedAt: timestampSchema,
})
    .strict();
/** A guided cookbook's approval of one recipe, exactly as it stood at this revision. */
export const variantRecipeApprovalV1Schema = z
    .object({
    version: z.literal(1),
    recipeId: z.string().min(1).max(160),
    revision: z.number().int().positive(),
    approvedBy: idSchema,
    approvedAt: timestampSchema,
})
    .strict();
//# sourceMappingURL=book-variant.js.map